import FinderService from "./FinderService"

import { categories as finderCategories, minQueryLength } from "./constants"
import { debounceAsync } from "~/lib/utils"
import { notify } from "~/lib/bugsnag"

export const makeDefaultState = () => ({
    searchText: "",
    selectedCategoryId: null, // string | null

    items: [],
    nextCursor: null,
    status: "ready", // "ready" | "refreshing"
    end: false,

    // Used to discard HTTP responses from any but the latest HTTP request.
    requestId: 0,
})

export const getters = {
    categories: (_state, _getters, rootState) => {
        const categories = []

        for (const categoryId of rootState.platform.entitiesFinder) {
            const category = finderCategories.find(c => c.id === categoryId)
            if (category) {
                categories.push(category)
            } else {
                notify(new Error(`Don't know: ${categoryId}`))
            }
        }

        return categories
    },
}

export const mutations = {
    setSearchText(state, searchText) {
        state.searchText = searchText
    },

    setSelectedCategoryId(state, categoryId) {
        state.selectedCategoryId = categoryId
    },

    setStatus(state, status) {
        state.status = status
    },

    replaceItem(state, { index, item }) {
        state.items.splice(index, 1, item)
    },

    setItems(state, { items, nextCursor, end }) {
        state.items = items
        state.nextCursor = nextCursor
        state.end = end
    },

    incRequestId(state) {
        state.requestId += 1
    },

    clearModule(state) {
        Object.assign(state, makeDefaultState())
    },
}

export const makeActions = (finderService = new FinderService()) => ({
    async setSearchText({ commit, dispatch }, searchText) {
        commit("setSearchText", searchText)
        if (searchText.trim().length >= minQueryLength) {
            await dispatch("_refresh")
        } else {
            commit("setItems", {
                items: [],
                nextCursor: null,
                end: false,
            })
            commit("incRequestId") // Invalidate unfinished requests
        }
    },

    async selectCategory({ state, commit, dispatch }, categoryId) {
        commit("setSelectedCategoryId", categoryId)
        if (state.searchText.trim().length >= minQueryLength) {
            await dispatch("_refresh")
        }
    },

    async loadMore({ state, getters, commit }) {
        commit("setStatus", "refreshing")
        commit("incRequestId")

        const {
            searchText,
            selectedCategoryId,
            nextCursor: cursor,
            requestId,
        } = state

        const category = getters.categories.find(
            c => c.id === selectedCategoryId
        )
        const itemType = category?.itemType

        try {
            const { items, nextCursor } = await finderService.fetchItems({
                searchText,
                itemType,
                cursor,
            })
            if (state.requestId === requestId) {
                commit("setItems", {
                    items: state.items.concat(items),
                    nextCursor,
                    end: !nextCursor,
                })
            }
        } finally {
            if (state.requestId === requestId) {
                commit("setStatus", "ready")
            }
        }
    },

    _refresh: debounceAsync(async ({ state, getters, commit }) => {
        commit("setStatus", "refreshing")
        commit("incRequestId")

        const { searchText, selectedCategoryId, requestId } = state

        const category = getters.categories.find(
            c => c.id === selectedCategoryId
        )
        const itemType = category?.itemType

        try {
            const { items, nextCursor } = await finderService.fetchItems({
                searchText,
                itemType,
            })
            if (state.requestId === requestId) {
                commit("setItems", {
                    items,
                    nextCursor,
                    end: !nextCursor,
                })
            }
        } finally {
            if (state.requestId === requestId) {
                commit("setStatus", "ready")
            }
        }
    }, 500),

    updateItem({ state, commit }, item) {
        const index = state.items.findIndex(
            i =>
                item._meta.type === i._meta.type &&
                item[item._meta.key] === i[i._meta.key]
        )
        if (index !== -1) commit("replaceItem", { index, item })
    },
})

export default {
    namespaced: true,
    state: makeDefaultState(),
    getters,
    mutations,
    actions: makeActions(),
}
